import { ReactElement, useRef, useState, useEffect } from "react";
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import CustomSelect from '@components/CustomSelect';
import Checkbox from '@components/Checkbox';
import TextBanner from '@components/TextBanner';

import { getLabelFallback, mergeRefs, onKeyPressOnlyNumber, onKeyPressEmail, onKeyPressAlpha, onKeyPressAlphaNumeric, loadScript, getFormattedPhone, getMd5Phone, getSha256Phone } from "@utils/utils";
import { useIMask } from "react-imask";

import Error from '@images/svg-icons/error.svg';
import Success from '@images/svg-icons/success.svg';

import { states, FIELD_PATTERN } from '@utils/const';

type BossDeliveryAddressForm = {
    onSubmit: (value) => void;
    addressErrors: string;
}

const BossDeliveryAddressForm = (props: BossDeliveryAddressForm) : ReactElement => {

    const {
        onSubmit,
        addressErrors
    } = props;

    const address = useSelector(state => state.cart.address);
    const cart = useSelector(state => state.cart.cart);

    const methods = useForm({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldFocusError: false
    });

    const imaskPhoneNumber = useIMask({ mask: '(000) 000-0000' });
    const billingZipCode = useRef(null);
    const deliveryPhone = useRef(null);

    const [hasError, setHasError] = useState('');

    const [isValidEmail, setIsValidEmail] = useState(!methods.formState.errors['deliveryEmail'] && !!methods.watch('deliveryEmail') && methods.watch('deliveryEmail').length > 0);
    const [isValidDeliveryFirstName, setIsValidDeliveryFirstName] = useState(!methods.formState.errors['deliveryFirstName'] && !!methods.watch('deliveryFirstName') && methods.watch('deliveryFirstName').length > 0);
    const [isValidDeliveryLastName, setIsValidDeliveryLastName] = useState(!methods.formState.errors['deliveryLastName'] && !!methods.watch('deliveryLastName') && methods.watch('deliveryLastName').length > 0);
    const [isValidDeliveryPhone, setIsValidDeliveryPhone] = useState(!methods.formState.errors['deliveryPhone'] && !!methods.watch('deliveryPhone') && methods.watch('deliveryPhone').length > 0);

    const [isValidBillingFirstName, setIsValidBillingFirstName] = useState(!methods.formState.errors['billingFirstName'] && !!methods.watch('billingFirstName') && methods.watch('billingFirstName').length > 0);
    const [isValidBillingLastName, setIsValidBillingLastName] = useState(!methods.formState.errors['billingLastName'] && !!methods.watch('billingLastName') && methods.watch('billingLastName').length > 0);
    const [isValidBillingAddress, setIsValidBillingAddress] = useState(!methods.formState.errors['billingAddress'] && !!methods.watch('billingAddress') && methods.watch('billingAddress').length > 0);
    const [isValidBillingAddress2, setIsValidBillingAddress2] = useState(!methods.formState.errors['billingAddress2'] && !!methods.watch('billingAddress2') && methods.watch('billingAddress2').length > 0);
    const [isValidBillingZipCode, setIsValidBillingZipCode] = useState(!methods.formState.errors['billingZipCode'] && !!methods.watch('billingZipCode') && methods.watch('billingZipCode').length > 0);
    const [isValidBillingCity, setIsValidBillingCity] = useState(!methods.formState.errors['billingCity'] && !!methods.watch('billingCity') && methods.watch('billingCity').length > 0);
    const [isValidBillingState, setIsValidBillingState] = useState(!methods.formState.errors['billingState'] && !!methods.watch('billingState') && methods.watch('billingState').length > 0);

    const [defaultValueBillingState, setDefaultValueBillingState] = useState(null);

    const [focusedField, setFocusedField] = useState(null);
    const [showOptionalBillingAddress, setShowOptionalBillingAddress] = useState(false);

    const [smsChecked, setSmsChecked] = useState(getSmsCheckboxValue());

    const config = useSelector(state => state.cart.config);

    let autoComplete;

    const clickOnSmsCheckbox = () => {
        setSmsChecked(smsChecked => smsChecked = !smsChecked);
        window.smsChecked=smsChecked;
    }

    useEffect(() => {
        if(!!address && !!cart) {
            const defaultValues = getDefaultValues(address, cart);
            methods.setValue('deliveryEmail', defaultValues['deliveryEmail']);
            methods.setValue('deliveryFirstName', defaultValues['deliveryFirstName']);
            methods.setValue('deliveryLastName', defaultValues['deliveryLastName']);
            !!defaultValues['deliveryEmail'] && setIsValidEmail(true);
            !!defaultValues['deliveryFirstName'] && setIsValidDeliveryFirstName(true);
            !!defaultValues['deliveryLastName'] && setIsValidDeliveryLastName(true);
            
            if(!!address.deliveryAddress.phone1 && !!deliveryPhone.current) {
                deliveryPhone.current.value = defaultValues['deliveryPhone'];
                methods.setValue('deliveryPhone', defaultValues['deliveryPhone']);
                setIsValidDeliveryPhone(true);
            }
            methods.setValue('billingFirstName', defaultValues['billingFirstName']);
            methods.setValue('billingLastName', defaultValues['billingLastName']);
            methods.setValue('billingAddress', defaultValues['billingAddress']);
            methods.setValue('billingAddress2', defaultValues['billingAddress2']);
            methods.setValue('billingCity', defaultValues['billingCity']);
            methods.setValue('billingState', defaultValues['billingState']);
            setDefaultValueBillingState(defaultValues['billingState']);

            !!defaultValues['billingFirstName'] && setIsValidBillingFirstName(true);
            !!defaultValues['billingLastName'] &&setIsValidBillingLastName(true);
            !!defaultValues['billingAddress'] && setIsValidBillingAddress(true);
            !!defaultValues['billingAddress2'] && defaultValues['billingAddress2'].length > 0 ? setIsValidBillingAddress2(true) : null;
            !!defaultValues['billingCity'] && setIsValidBillingCity(true);
            !!defaultValues['billingState'] && setIsValidBillingState(true);

            if(!!address && !!address.billingAddress && !!address.billingAddress.zipCode && !!billingZipCode.current) {
                billingZipCode.current.value = address.billingAddress.zipCode;
                methods.setValue('billingZipCode', address.billingAddress.zipCode);
                setIsValidBillingZipCode(true);
            }
        }
    }, [address, cart]);

    useEffect(() => {
        methods.setValue('subscribe-sms', smsChecked+"");
        localStorage.setItem('subscribe-sms', smsChecked+"");
    }, [smsChecked]);

    /* Autocomplete on billing zip code */
    useEffect(() => {
        window.addEventListener('load', function () {
            document.getElementById('billingZipCode').addEventListener('click', () => {
                document.getElementById('billingZipCode').setAttribute('autocomplete', 'new-password');
            });
        });
    }, []);

    /* Handle visibility of billing address 2 */
    useEffect(() => {
        if(showOptionalBillingAddress) {
            document.getElementById('billingAddress2')?.addEventListener('blur', () => {
                setIsValidBillingAddress2(!!methods.formState.errors['billingAddress2'] ? false : true);
            });

            return () => {
                document.getElementById('billingAddress2')?.removeEventListener('blur', () => {});
            }
        }
    }, [showOptionalBillingAddress]);

    function handleScriptLoad(autoComplete, autoCompleteRef, type) {
        autoComplete = new window.google.maps.places.Autocomplete(
          autoCompleteRef.current,
          { types: ["(regions)"], componentRestrictions: { country: ["us", "pr"] } }
        );
    
        autoComplete.addListener("place_changed", () =>
          { 
            const place = autoComplete.getPlace();
            const zipCode = place.address_components.find(item => item.types[0] === 'postal_code').long_name;
            const city = place.address_components.find(item => item.types[0] === 'locality').long_name;
            const state = (place.address_components.find(item => item.types[0] === 'country').short_name == "PR") ? place.address_components.find(item => item.types[0] === 'country').short_name : place.address_components.find(item => item.types[0] === 'administrative_area_level_1').short_name;
            methods.setValue(type + 'ZipCode', zipCode);
            methods.setValue(type + 'State', state);
            methods.setValue(type + 'City', city);

            document.getElementById(type + 'ZipCode').value = zipCode;
            document.getElementById(type + 'State').value = state;
            document.getElementById(type + 'City').value = city;

            
            billingZipCode.current.value = zipCode;
            setDefaultValueBillingState(state);
            setIsValidBillingZipCode(true);
            setIsValidBillingCity(true);
            setIsValidBillingState(true);
            methods.trigger('billingZipCode');
            methods.trigger('billingCity');
            methods.trigger('billingState')
          }
        );
    }

    //load autocomplete
    useEffect(() => {
        if(config.autocomplete === 'on') {
            if(typeof(window.google) === 'undefined' && typeof(window.google.maps) === 'undefined' && typeof(window.google.maps.places) === 'undefined') {
                loadScript(
                `https://maps.googleapis.com/maps/api/js?key=AIzaSyBYKlTpu53k5g-bkUSwS5ZHOJbttikH800&libraries=places`,
                //`https://maps.googleapis.com/maps/api/js?key=AIzaSyDTDrO2IcsgqiSzz7D-7VihEdJllXPoHEM&libraries=places`,
                () => {
                    handleScriptLoad(autoComplete, billingZipCode, 'billing');
                }
              );
            } else {
                handleScriptLoad(autoComplete, billingZipCode, 'billing');
            }
            

              window.gm_authFailure = () => {
                //do not disable inputs if Google apis autocomplete gets error
                const inputs = document.querySelectorAll<HTMLInputElement>('.pac-target-input.gm-err-autocomplete:disabled');
                for (let input of Array.from(inputs)) {
                  input.disabled = false;
                  input.placeholder = "";
                }
        
                console.error('General Error with Google Api ');
                return false;
              }
        
              return () => {
                //remove gm_authFailure method
                window.gm_authFailure = undefined;
              }
        }
        
     }, []);


    //focus field
    useEffect(() => {
        const err = document.getElementsByClassName('not-valid');
        const el = Array.from(err)?.find(e => e.nodeName === 'INPUT');
        setFocusedField(el);
        el?.scrollIntoView({behavior: 'smooth', block: 'center'});
    });

    useEffect(() => {
        focusedField?.focus();
    }, [focusedField]);

    return (
        <>
            {(hasError === 'nickname' || hasError === 'generic') &&
               <TextBanner classes={"isError"}>
                    <div className="cart-error">
                        <span>{ getLabelFallback(hasError.toUpperCase() + '_ERROR_LABEL') }</span> 
                    </div>
                </TextBanner>
            }
            <FormProvider {...methods}>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    setFocusedField(null);
                    methods.handleSubmit(onSubmit)();
                }}>
                    <div className="boss-delivery-address-form">
                        <div className={`input custom ${methods.formState.errors['deliveryEmail'] ? 'not-valid': isValidEmail ? 'valid' : ''}`}>
                            <input 
                                {...methods.register('deliveryEmail', {required: true, pattern:{
                                    value: FIELD_PATTERN.EMAIL,
                                    message: getLabelFallback('EMAIL_ERROR_LABEL')
                                }, validate: (value) => {if(!!value) {
                                    setIsValidEmail(true);
                                }else {
                                    setIsValidEmail(false);
                                }
                                return !!value;
                                }})}
                                name={'deliveryEmail'}        
                                id={'deliveryEmail'}    
                                maxLength={64}
                                    placeholder={getLabelFallback('DELIVERY_EMAIL_LABEL')}
                                className={`input-element ${methods.formState.errors['deliveryEmail'] ? 'not-valid': isValidEmail ? 'valid' : ''}`}
                                onKeyPress={(e) => {onKeyPressEmail(e)}}
                                aria-invalid={methods.formState.errors['deliveryEmail'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_EMAIL_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{methods.formState.errors['deliveryEmail'] ? <Error /> :  isValidEmail ? <Success /> : ''}</div>
                            </div>
                            {!!methods.formState.errors['deliveryEmail'] && <span className="input-error">{methods.formState.errors['deliveryEmail'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'e-mail')}</span>}
                        </div>

                        <div className={`input custom ${methods.formState.errors['deliveryFirstName'] ? 'not-valid': isValidDeliveryFirstName ? 'valid' : ''}`}>
                            <input 
                                {...methods.register('deliveryFirstName', {required: true, 
                                    validate: (value) => {
                                        if(!!value) {
                                            setIsValidDeliveryFirstName(true);
                                        } else {
                                            setIsValidDeliveryFirstName(false);
                                        }

                                        return !!value;
                                    }
                                })}
                                name={'deliveryFirstName'} 
                                id={'deliveryFirstName'}    
                                maxLength={32}
                                placeholder={getLabelFallback('DELIVERY_FIRST_NAME_LABEL')}
                                className={`input-element ${methods.formState.errors['deliveryFirstName'] ? 'not-valid': isValidDeliveryFirstName ? 'valid' : ''}`}
                                onKeyPress={(e) => onKeyPressAlpha(e)}
                                aria-invalid={methods.formState.errors['deliveryFirstName'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_FIRST_NAME_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{methods.formState.errors['deliveryFirstName'] ? <Error /> : isValidDeliveryFirstName ? <Success /> : ''}</div>
                            </div>
                            {!!methods.formState.errors['deliveryFirstName'] && <span className="input-error">{methods.formState.errors['deliveryFirstName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'first name')}</span>}
                        </div>

                        <div className={`input custom ${methods.formState.errors['deliveryLastName'] ? 'not-valid': isValidDeliveryLastName ? 'valid' : ''}`}>
                            <input 
                                {...methods.register('deliveryLastName', {required: true, 
                                    validate: (value) => {
                                        if(!!value) {
                                            setIsValidDeliveryLastName(true);
                                        } else {
                                            setIsValidDeliveryLastName(false);
                                        }

                                        return !!value;
                                    }})}
                                name={'deliveryLastName'}
                                id={'deliveryLastName'}        
                                maxLength={32}
                                placeholder={getLabelFallback('DELIVERY_LAST_NAME_LABEL')}
                                className={`input-element ${methods.formState.errors['deliveryLastName'] ? 'not-valid': isValidDeliveryLastName ? 'valid' : ''}`}
                                onKeyPress={(e) => onKeyPressAlpha(e)}
                                aria-invalid={methods.formState.errors['deliveryLastName'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_LAST_NAME_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{methods.formState.errors['deliveryLastName'] ? <Error /> : isValidDeliveryLastName ? <Success /> : ''}</div>
                            </div>
                            {!!methods.formState.errors['deliveryLastName'] && <span className="input-error">{methods.formState.errors['deliveryLastName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'last name')}</span>}
                        </div> 
                            
                        <div className={`input custom big ${methods.formState.errors['deliveryPhone'] ? 'not-valid': isValidDeliveryPhone ? 'valid' : ''}`}>
                            <input 
                                type={'tel'}
                                ref={mergeRefs(deliveryPhone, imaskPhoneNumber.ref, methods.register('deliveryPhone', {required: true, 
                                    pattern: {
                                        value: /^\(\d{3}\) \d{3}-\d{4}/,
                                        message: getLabelFallback('INVALID_FIELD_VALUE').replace('{FIELD_NAME}', 'phone number')
                                    },
                                    validate: (value) => {
                                        if(!!value) {
                                            setIsValidDeliveryPhone(true);
                                        } else {
                                            setIsValidDeliveryPhone(false);
                                        }

                                        return !!value;
                                    }}))}
                                name={'deliveryPhone'}
                                id={'deliveryPhone'}          
                                maxLength={15}
                                placeholder={getLabelFallback('DELIVERY_PHONE_LABEL')}
                                className={`input-element ${methods.formState.errors['deliveryPhone'] ? 'not-valid': isValidDeliveryPhone ? 'valid' : ''}`}
                                onChange={(e) => { 
                                    methods.setValue('deliveryPhone', e.target.value);
                                    deliveryPhone.current.value = e.target.value;
                                    document.getElementById('deliveryPhone').value = e.target.value;
                                }}
                                aria-invalid={methods.formState.errors['deliveryPhone'] ? 'true' : 'false'}
                                onBlur={(e) => {
                                    methods.setValue('deliveryPhone', e.target.value);
                                    deliveryPhone.current.value = e.target.value;
                                    document.getElementById('deliveryPhone').value = e.target.value;
                                    methods.trigger('deliveryPhone');
                                }}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_PHONE_LABEL')}</label>
                            <span className="input-claim">{getLabelFallback('DELIVERY_PHONE_CLAIM')}</span>
                            <div className="input-icons">
                                <div className="input-icon">{methods.formState.errors['deliveryPhone'] ? <Error /> : isValidDeliveryPhone ? <Success /> : ''}</div>
                            </div>
                            {!!methods.formState.errors['deliveryPhone'] && <span className="input-error">{methods.formState.errors['deliveryPhone'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'phone')}</span>}
                        </div>

                        <>
                            <h3 className="boss-delivery-address-form-title">{ getLabelFallback('BILLING_ADDRESS_LABEL')}</h3>
                            <div className={`input custom ${methods.formState.errors['billingFirstName'] ? 'not-valid': isValidBillingFirstName ? 'valid' : ''}`}>
                                <input 
                                    {...methods.register('billingFirstName', {required: true, 
                                        validate: (value) => {
                                            if(!!value) {
                                                setIsValidBillingFirstName(true);
                                            } else {
                                                setIsValidBillingFirstName(false);
                                            }
            
                                            return !!value;
                                    }})}
                                    name={'billingFirstName'} 
                                    id={'billingFirstName'}       
                                    maxLength={32}
                                    placeholder={getLabelFallback('BILLING_FIRST_NAME_LABEL')}
                                    className={`input-element ${methods.formState.errors['billingFirstName'] ? 'not-valid' : isValidBillingFirstName ? 'valid' : ''}`}
                                    onKeyPress={(e) => onKeyPressAlpha(e)}
                                    aria-invalid={methods.formState.errors['billingFirstName'] ? 'true' : 'false'}
                                />
                                <label className="input-label">{getLabelFallback('BILLING_FIRST_NAME_LABEL')}</label>
                                <div className="input-icons">
                                    <div className="input-icon">{methods.formState.errors['billingFirstName'] ? <Error /> : isValidBillingFirstName ? <Success /> : ''}</div>
                                </div>
                                {!!methods.formState.errors['billingFirstName'] && <span className="input-error">{methods.formState.errors['billingFirstName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'first name')}</span>}
                            </div>

                            <div className={`input custom ${methods.formState.errors['billingLastName'] ? 'not-valid': isValidBillingLastName ? 'valid' : ''}`}>
                                <input 
                                    {...methods.register('billingLastName', {required: true, 
                                        validate: (value) => {
                                            if(!!value) {
                                                setIsValidBillingLastName(true);
                                            } else {
                                                setIsValidBillingLastName(false);
                                            }
            
                                            return !!value;
                                        }})}
                                    name={'billingLastName'}
                                    id={'billingLastName'}        
                                    maxLength={32}
                                    placeholder={getLabelFallback('BILLING_LAST_NAME_LABEL')}
                                    className={`input-element ${methods.formState.errors['billingLastName'] ? 'not-valid' : isValidBillingLastName ? 'valid' : ''}`}
                                    onKeyPress={(e) => onKeyPressAlpha(e)}
                                    aria-invalid={methods.formState.errors['billingLastName'] ? 'true' : 'false'}
                                />
                                <label className="input-label">{getLabelFallback('BILLING_LAST_NAME_LABEL')}</label>
                                <div className="input-icons">
                                    <div className="input-icon">{methods.formState.errors['billingLastName'] ? <Error /> : isValidBillingLastName ? <Success /> : ''}</div>
                                </div>
                                {!!methods.formState.errors['billingLaName'] && <span className="input-error">{methods.formState.errors['billingLastName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'last name')}</span>}
                            </div>

                            <div className={`input custom big ${methods.formState.errors['billingAddress'] ? 'not-valid': isValidBillingAddress ? 'valid' : ''}`}>
                                <input 
                                    {...methods.register('billingAddress', {required: true, 
                                        validate: (value) => {
                                            if(!!value) {
                                                setIsValidBillingAddress(true);
                                            } else {
                                                setIsValidBillingAddress(false);
                                            }
            
                                            return !!value;
                                        }})}
                                    name={'billingAddress'}
                                    id={'billingAddress'}          
                                    maxLength={50}
                                    placeholder={getLabelFallback('BILLING_ADDRESS1_LABEL')}
                                    className={`input-element ${methods.formState.errors['billingAddress'] ? 'not-valid' : isValidBillingAddress ? 'valid' : ''}`}
                                    onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                    aria-invalid={methods.formState.errors['billingAddress'] ? 'true' : 'false'}
                                            
                                />
                                <label className="input-label">{getLabelFallback('BILLING_ADDRESS1_LABEL')}</label>
                                <div className="input-icons">
                                    <div className="input-icon">{methods.formState.errors['billingAddress'] ? <Error /> : isValidBillingAddress ? <Success /> : ''}</div>
                                </div>
                                {!!methods.formState.errors['billingAddress'] && <span className="input-error">{methods.formState.errors['billingAddress'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'street address')}</span>}
                            </div>

                            {!showOptionalBillingAddress &&
                                <div className="delivery-address-form-details">
                                    <button className="primary-button" onClick={(e) => {e.preventDefault(); setShowOptionalBillingAddress(showOptionalBillingAddress => showOptionalBillingAddress = !showOptionalBillingAddress);}}>{ getLabelFallback('DELIVERY_DETAILS_LABEL') }</button>
                                </div>
                            }

                            {showOptionalBillingAddress &&
                                <div className={`input custom big ${methods.formState.errors['billingAddress2'] ? 'not-valid': isValidBillingAddress2 ? 'valid' : ''}`}>
                                    <input 
                                        {...methods.register('billingAddress2',{
                                            validate: (value) => {if(!!value) {setIsValidBillingAddress2(true); return !!value;} return true;}})}
                                        name={'billingAddress2'}
                                        id={'billingAddress2'}            
                                        maxLength={50}
                                        placeholder={getLabelFallback('BILLING_ADDRESS2_LABEL')}
                                        className={`input-element ${methods.formState.errors['billingAddress2'] ? 'not-valid' : isValidBillingAddress2 ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                        aria-invalid={methods.formState.errors['billingAddress2'] ? 'true' : 'false'}
                                    />
                                    <label className="input-label">{getLabelFallback('BILLING_ADDRESS2_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{methods.formState.errors['billingAddress2'] ? <Error /> : isValidBillingAddress2 ? <Success /> : ''}</div>
                                    </div>
                                    {!!methods.formState.errors['billingAddress2'] && <span className="input-error">{methods.formState.errors['billingAddress2'].message}</span>}
                                </div>
                            }
                        
                            <div className={`input custom big ${methods.formState.errors['billingZipCode'] ? 'not-valid': isValidBillingZipCode ? 'valid' : ''}`}>
                                <input 
                                    type={'tel'}
                                    ref={mergeRefs(billingZipCode, methods.register('billingZipCode', {required: true, validate: value => !value.startsWith('000') ||  "The Zipcode is invalid"}))}
                                    name={'billingZipCode'}
                                    id={'billingZipCode'}       
                                    maxLength={5}
                                    placeholder={getLabelFallback('BILLING_ZIPCODE_LABEL')}
                                    className={`input-element ${methods.formState.errors['billingZipCode'] ? 'not-valid' : isValidBillingZipCode ? 'valid' : ''}`}
                                    onKeyPress={(e) => onKeyPressOnlyNumber(e)}
                                    aria-invalid={methods.formState.errors['billingZipCode'] ? 'true' : 'false'}
                                    onChange={(e) => {
                                        methods.setValue('billingZipCode', e.target.value)
                                        billingZipCode.current.value = e.target.value;
                                        document.getElementById('billingZipCode').value = e.target.value;
                                    }}
                                    onBlur={(e) => {
                                        methods.setValue('billingZipCode', e.target.value)
                                        billingZipCode.current.value = e.target.value;
                                        document.getElementById('billingZipCode').value = e.target.value;
                                        methods.trigger('billingZipCode');
                                    }}
                                />
                                <label className="input-label">{getLabelFallback('BILLING_ZIPCODE_LABEL')}</label>
                                <div className="input-icons">
                                    <div className="input-icon">{methods.formState.errors['billingZipCode'] ? <Error /> : isValidBillingZipCode ? <Success /> : ''}</div>
                                </div>
                                {!!methods.formState.errors['billingZipCode'] && <span className="input-error">{methods.formState.errors['billingZipCode'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'zipcode')}</span>}
                            </div>

                            <div className="boss-delivery-address-form-row">
                                <div className={`input custom ${methods.formState.errors['billingCity'] ? 'not-valid': isValidBillingCity ? 'valid' : ''}`}>
                                    <input 
                                        {...methods.register('billingCity', {required: true, 
                                            validate: (value) => {
                                                if(!!value) {
                                                    setIsValidBillingCity(true);
                                                } else {
                                                    setIsValidBillingCity(false);
                                                }
                
                                                return !!value;
                                            }})}
                                        name={'billingCity'}
                                        id={'billingCity'}        
                                        maxLength={128}
                                        placeholder={getLabelFallback('BILLING_CITY_LABEL')}
                                        className={`input-element ${methods.formState.errors['billingCity'] ? 'not-valid' : isValidBillingCity ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                        aria-invalid={methods.formState.errors['billingCity'] ? 'true' : 'false'}
                                    />
                                    <label className="input-label">{getLabelFallback('BILLING_CITY_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{methods.formState.errors['billingCity'] ? <Error /> : isValidBillingCity ? <Success /> : ''}</div>
                                    </div>
                                    {!!methods.formState.errors['billingCity'] && <span className="input-error">{methods.formState.errors['billingCity'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'city')}</span>}
                                </div>

                                <CustomSelect 
                                    id={"billingStateSelect"}
                                    inputId={"billingState"}
                                    placeholder={getLabelFallback('DELIVERY_STATE_LABEL')}
                                    topLabel={getLabelFallback('BILLING_STATE_LABEL')}
                                    options={states}
                                    onChange={ (value) => methods.setValue('billingState', value)}
                                    defaultValue={defaultValueBillingState}
                                    aria-invalid={methods.formState.errors['billingState'] ? 'true' : 'false'}
                                />
                                <input {...methods.register('billingState', {required: true})} type="hidden" id="billingState" />
                            </div>
                        </>

                        {config.sms.enabled === true && 
                            <Checkbox id={"subscribe-sms"} 
                                label={getLabelFallback('SMS_CHECKBOX_LABEL')} 
                                checked={smsChecked}
                                onChange={() => {clickOnSmsCheckbox()}}/>
                        }

                        {config.sms.enabled === true &&
                            <div className="sms-terms-conditions">
                                <span dangerouslySetInnerHTML={{__html: getLabelFallback('SMS_TERMS_LABEL')
                                    .replace('{CONDITIONS}', '<a class="terms-privacy" target="_blank" aria-label="'+getLabelFallback('SMS_TERMS_CONDITION_LABEL')+'" href="'+ config.linkConditionUseSale +'">'+ getLabelFallback('SMS_TERMS_CONDITION_LABEL')+'</a> & <a class="terms-privacy" target="_blank" aria-label="'+getLabelFallback('SMS_TERMS_PRIVACY_LABEL')+'" href="/gl-us/privacy-policy">'+ getLabelFallback('SMS_TERMS_PRIVACY_LABEL')+'</a>')}}>
                                </span>
                            </div>
                        }

                        <button data-element-id="Form_Proceed" className="violet-button" type="submit">{getLabelFallback('PAYMENT_BUTTON_LABEL')}</button>
                        
                        <div className="boss-delivery-address-form-claim">
                            <span dangerouslySetInnerHTML={{__html: getLabelFallback('PAYMENT_ACCEPTANCE_RIGHTS_LABEL')
                                .replace('{SITE_NAME}', getLabelFallback('SITE_NAME'))
                                .replace('{CONDITIONS}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('CONDITIONS_USE_SALE_LABEL')+'" href="'+ config.linkConditionUseSale +'">'+ getLabelFallback('CONDITIONS_USE_SALE_LABEL')+'</a>')
                                .replace('{PRIVACY}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('PRIVACY_NOTICE_LABEL')+'" href="'+ config.linkPrivacyNotice +'">'+ getLabelFallback('PRIVACY_NOTICE_LABEL')+'</a>')
                                .replace('{COOKIES}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('COOKIES_LABEL')+'" href="'+ config.linkCookieAndInternetAdvertising +'">'+ getLabelFallback('COOKIES_LABEL')+'</a>')}}>
                            </span>
                        </div>
                    </div>
                </form>
          </FormProvider>
        </>
    )
}


export default BossDeliveryAddressForm;

const getDefaultValues = (address, cart) => {
    if(address === null) return {};

    const birthdayFromCart = !!cart ? cart.orderItem.find(item => item.xitem_dateOfBirth) : null;

    const birthday = !!address.deliveryAddress.DATE_OF_BIRTH ? address.deliveryAddress.DATE_OF_BIRTH :
        !!cart && !!cart.orderItem && cart.orderItem.length > 0 ?
        !!birthdayFromCart ? birthdayFromCart.xitem_dateOfBirth : null
        : null;
    
    return {
        'deliveryEmail': address.deliveryAddress.email1,
        'deliveryFirstName': address.deliveryAddress.firstName,
        'deliveryLastName': address.deliveryAddress.lastName,
        'deliveryAddress': address.deliveryAddress.address1,
        'deliveryAddress2': address.deliveryAddress.address2,
        'deliveryBirthday': birthday,
        'deliveryCity': address.deliveryAddress.city,
        'deliveryPhone': !!address.deliveryAddress.phone1 ? getFormattedPhone(address.deliveryAddress.phone1) : '',
        'deliveryState': address.deliveryAddress.state,
        'deliveryZipCode': address.deliveryAddress.zipCode,
        'billingFirstName': address.billingAddress.firstName,
        'billingLastName': address.billingAddress.lastName,
        'billingAddress': address.billingAddress.address1,
        'billingAddress2': address.billingAddress.address2,
        'billingCity': address.billingAddress.city,
        'billingState': address.billingAddress.state,
        'billingZipCode': address.billingAddress.zipCode
    }
}

const getSmsCheckboxValue = () => {    
    if (localStorage.getItem('subscribe-sms') == 'true') {
        return true;
    } else {
        return false;
    }
}